import { useContext, useState } from 'react';
import { compose, flatten, map, path, pipe } from 'ramda';
import { branch, withProps } from 'recompose';
import { useMountEffect } from 'js/hooks/useMountEffect';
import withLeaderBoardsReducer from '../common/withLeaderBoardsReducer';
import withSummaryUpdates from '../common/withSummaryUpdates';
import { withFetchListActions } from '../withLeaderBoardList';
import { fetchActiveList, fetchHistoricalList, setListAction, updateListAction } from '../actions';
import { ListContext } from '../context';
import { withListContext } from '../withContext';
import { statuses } from '../constants';
import { NOTIFICATION_FIELDS } from './constants';
import {
    subscribeToDiscoveryPage
} from '../../PassTheParcel/Details/DetailedPage/network';
import { usePushSubscription } from 'server';
import { getLocationParam } from 'js/utils/url.util';

const customizeMpToLeaderboard = (parcel) => {
    parcel.isParcel = true;
    parcel.millisecondsTillLeaderboardEnd = parcel.eventEndTime * 1000 - Date.now();
    parcel.leaderboardStartTime = new Date(parcel.eventStartTime * 1000);
    parcel.leaderboardEndTime = new Date(parcel.eventEndTime * 1000);
    if (parcel.status === 'IDLE') {
        parcel.status = 'ACTIVE';
    }
    if (parcel.status === 'PENDING_REVIEW') {
        parcel.status = 'COMPLETED';
    }
    return parcel;
};

export const useFetchingLeaderBoards = (props) => {
    const { state: { leaderboards }, dispatch } = useContext(ListContext);
    const [error, setError] = useState();
    const {
        presentUpcomingRaces,
        presentOnAirRaces,
        presentEndedRaces,
        presentPendingRaces,
        maxEndedRacesQuantity,
        ...rest
    } = props;

    usePushSubscription(73016, () => (message) => {
        dispatch(updateListAction(customizeMpToLeaderboard(message.data)));
    });

    useMountEffect((isMounted) => {
        let promises = [];

        if (presentOnAirRaces || presentUpcomingRaces || presentPendingRaces) {
            promises = [
                ...promises, fetchActiveList({
                    ...rest,
                    filterByStatus: [
                        presentOnAirRaces && statuses.ACTIVE,
                        presentUpcomingRaces && statuses.NOT_STARTED,
                        presentPendingRaces && statuses.PENDING_REVIEW
                    ].filter(Boolean),
                    sortOptions: [{ fieldName: 'leaderboardEndTime', sortDirection: 'desc' }]
                })
            ];
        }

        if (presentEndedRaces) {
            promises = [
                ...promises,
                fetchHistoricalList({
                    ...rest,
                    startFrom: 0,
                    count: maxEndedRacesQuantity,
                    sortOptions: [{ fieldName: 'leaderboardEndTime', sortDirection: 'desc' }]
                })
            ];
        }

        const resolveCallback = (mpData) => {
            Promise.allSettled(promises)
                .then((returns) => {
                    const newResponses = returns.map(e => e.value);
                    let lbMpArray;
                    if (mpData) {
                        lbMpArray = [...newResponses, mpData];
                    } else {
                        lbMpArray = [...newResponses];
                    }
                    const responses = lbMpArray.filter(response => response.ID !== 10);
                    let leaderBoardResponses = [];
                    let parcelResponse = [];
                    responses.forEach((response) => {
                        const isNoService = response && response.message
                            && response.message.includes('73010');
                        if (!isNoService && response.data.parcels !== undefined) {
                            parcelResponse = [...parcelResponse, response];
                        } else if (!isNoService && response.data.leaderboards !== undefined) {
                            leaderBoardResponses = [...leaderBoardResponses, response];
                        }
                    });
                    if (isMounted()) {
                        let racesLeaderBoard = mergeResponses(leaderBoardResponses);
                        const racesParcels = mergeResponsesParcel(parcelResponse);
                        racesParcels.forEach((parcel) => {
                            racesLeaderBoard = [...racesLeaderBoard, customizeMpToLeaderboard(parcel)];
                        });
                        dispatch(setListAction(racesLeaderBoard));
                    }
                })
                .catch(({ data }) => isMounted() && setError(data));
        };

        const mergeResponses = pipe(
            map(path(['data', 'leaderboards'])),
            flatten
        );
        const mergeResponsesParcel = pipe(
            map(path(['data', 'parcels'])),
            flatten
        );

        if (getLocationParam('mp') !== '0') {
            subscribeToDiscoveryPage().then((res) => {
                resolveCallback(res);
            })
                .catch(() => {
                    resolveCallback();
                });
        } else {
            resolveCallback();
        }
    }, []);

    return { leaderboards, error };
};

export const withFetch = compose(
    withFetchListActions,
    withProps(useFetchingLeaderBoards)
);

export const withNotificationFieldsWhitelist = withProps(
    () => ({
        notificationFieldsWhitelist: NOTIFICATION_FIELDS
    })
);

export default compose(
    withLeaderBoardsReducer,
    withListContext,
    withFetch,
    withNotificationFieldsWhitelist,
    branch(
        ({ presentUpcomingRaces, presentOnAirRaces, presentEndedRaces, presentPendingRaces }) =>
            presentUpcomingRaces ||
            presentOnAirRaces ||
            presentPendingRaces ||
            presentEndedRaces,
        withSummaryUpdates
    )
);
